import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SalesOpportunitiesService } from '../../../../../data-services/sales-opportunities.service';
import { CommonModule } from '@angular/common';
import { AutomationReplacementChipsComponent } from '../../../../../common/components';
import { StaticSelectorService } from '../../../../../data-services/static-selector.service';
import { PipelineInterface } from '@vendasta/sales-opportunities';
import { DynamicOptionsComponent } from '../../../filters';

export interface SelectedPipeline {
  pipelineId: string;
  stageId: number;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, ReactiveFormsModule, AutomationReplacementChipsComponent],
  selector: 'automata-pipeline-selector',
  styleUrls: ['./pipeline-selector.component.scss'],
  templateUrl: './pipeline-selector.component.html',
})
export class PipelineSelectorComponent implements OnInit, OnDestroy, DynamicOptionsComponent {
  @Input() selectedPipelineId = '';
  @Input() selectedStageId: number | null = null;
  @Input() required = true;
  @Input() includeAnyStageOption = false;

  @Output() loaded: EventEmitter<null> = new EventEmitter<null>();

  pipelineControl = new FormControl();
  stageProbabilityControl = new FormControl();

  stageOptions: Map<number | string, string> = new Map<number, string>();
  stageOptionsDataService = new StaticSelectorService<number | string>(this.stageOptions, this.translateService);

  previousPipelineId = '';

  destroyed$$: Subject<boolean> = new Subject<boolean>();

  // filter interface
  @Input() initialValue: any[];
  selectedValue$ = this.pipelineControl.valueChanges;

  constructor(
    protected readonly salesOpportunitiesService: SalesOpportunitiesService,
    protected readonly translateService: TranslateService,
    protected readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.initialValue) {
      this.selectedPipelineId = this.initialValue[0];
      this.selectedStageId = this.initialValue[1];
    }

    this.previousPipelineId = this.selectedPipelineId;

    //Update the stage options
    combineLatest([this.pipelineControl.valueChanges, this.salesOpportunitiesService.pipelines$])
      .pipe(takeUntil(this.destroyed$$))
      .subscribe(([pipelineId, pipelines]) => {
        if (pipelineId !== this.previousPipelineId) {
          this.stageProbabilityControl.setValue(null);
          this.stageOptions.clear();
          this.cdr.detectChanges();
          this.previousPipelineId = pipelineId;
        }
        this.assignStageOptions(pipelineId, pipelines);
      });

    this.stageProbabilityControl.setValue(this.selectedStageId);
    this.pipelineControl.setValue(this.selectedPipelineId);

    if (this.required) {
      this.pipelineControl.setValidators(Validators.required);
      this.stageProbabilityControl.setValidators(Validators.required);
    }
  }

  private assignStageOptions(pipelineId: string, pipelines: PipelineInterface[]): void {
    if (this.includeAnyStageOption) {
      this.stageOptions.set(
        '*',
        this.translateService.instant('AUTOMATIONS.EDITOR.FILTERS.OPPORTUNITY_DATA.DATA_LABELS.ANY_STAGE'),
      );
    }
    const pipeline = pipelines.find((p) => p.pipelineId === pipelineId);
    pipeline?.stages?.forEach((stageOption) => {
      this.stageOptions.set(stageOption.probability || 0, stageOption.name || '');
    });
  }

  finishedLoading(): void {
    this.loaded.emit();
  }

  ngOnDestroy(): void {
    this.destroyed$$.next(true);
    this.destroyed$$.complete();
  }

  getSelectedPipeline(): SelectedPipeline {
    return {
      pipelineId: this.pipelineControl.value || '',
      stageId: this.stageProbabilityControl.value || 0,
    };
  }

  getControlsToValidateOnSave(): FormControl[] {
    return [this.pipelineControl, this.stageProbabilityControl];
  }

  isFormValid(): boolean {
    return !this.pipelineControl.invalid && !this.stageProbabilityControl.invalid;
  }

  // filter interface
  getSelectedValue(): string[] {
    return [this.pipelineControl.value, this.stageProbabilityControl?.value];
  }
}
